<template>
  <div>
    <ui-stage v-if="data">
      <ui-tabs v-model="tab" type="border-card">
        <ui-tab-pane name="main" label="编辑">
          <ui-alert type="info" :closable="false" v-if="data.status">
            当前状态：{{ data.status }}
          </ui-alert>
          <ui-alert type="warning" :closable="false" v-if="canAudit && data.status === '待审核'">
            <ui-button type="warning" @click="handleAudit">审核</ui-button>
          </ui-alert>
          <ui-flex row>
            <div style="flex: 0 0 auto; max-width: calc(100% - 230px)">
              <ui-admin-form
                :compact="compact"
                :readonly="readonly || !canEdit"
                :fields="vAttributes"
                :model="data"
                :module-url="moduleUrl"
                :with-delete="canDelete"
                @submit="handleSubmit"
              >
                <template
                  slot="field__attachments"
                  slot-scope="{ value, onInput, readonly, config }"
                >
                  <ui-uploads
                    :value="value"
                    @input="onInput"
                    :readonly="readonly || config.readonly"
                  />
                </template>
                <template v-slot:field__status="{ value, onInput, readonly, config }">
                  <template v-if="readonly">{{ value }}</template>
                  <ui-select
                    v-else
                    :value="value"
                    @input="onInput"
                    :disabled="!canChangeStatus || readonly || config.readonly"
                  >
                    <ui-option
                      v-for="status in statusOptions"
                      :key="status"
                      :label="status"
                      :value="status"
                    ></ui-option>
                  </ui-select>
                </template>
                <template v-slot:field__description="{ value, onInput, readonly, config }">
                  <template v-if="readonly">{{ value }}</template>
                  <ui-input
                    v-else
                    type="textarea"
                    :rows="4"
                    :value="value"
                    @input="onInput"
                    :disabled="!canEditDescription"
                  />
                </template>
                <template v-slot:field__rectificationIssue="{ value, onInput, readonly, config }">
                  <template v-if="readonly">{{ value }}</template>
                  <ui-input
                    v-else
                    type="textarea"
                    :rows="3"
                    :value="value"
                    @input="onInput"
                    :disabled="!canEditRectification"
                  />
                </template>
                <template v-slot:field__rectificationProgress="{ value, onInput, readonly }">
                  <template v-if="readonly">{{ value }}</template>
                  <ui-input
                    v-else
                    type="textarea"
                    :rows="3"
                    :value="value"
                    @input="onInput"
                    :disabled="!canEditRectification"
                  />
                </template>
                <template v-slot:field__rectificationPhotos="{ value, onInput, readonly }">
                  <ui-uploads
                    :value="value"
                    @input="onInput"
                    :readonly="readonly || !canEditRectification"
                    :limit="0"
                    button-text="点击上传整改照片"
                    hint="支持上传多张照片，展示整改现场情况"
                    :show-file-list="true"
                  />
                </template>
                <template v-slot:field__creatorId="scope">
                  <span>{{ data?.xCitizenComplaintCreator?.display_name }}</span>
                </template>
                <template v-slot:field__operatorId="scope">
                  <span>{{ data?.xCitizenComplaintOperator?.display_name }}</span>
                </template>
                <template v-slot:field__auditorId="scope">
                  <span>{{ data?.xCitizenComplaintAuditor?.display_name }}</span>
                </template>
              </ui-admin-form>
            </div>
            <div style="margin-left: 10px; flex: 0 0 200px">
              <div style="margin-top: 20px; padding: 20px; background: #f5f5f5; border-radius: 4px">
                <h4>操作说明</h4>
                <div v-if="isJsAdmin" style="margin-top: 10px">
                  <p><strong>建设单位管理人员：</strong></p>
                  <ul style="font-size: 12px; margin: 5px 0">
                    <li>可创建工单</li>
                    <li>可删除未处理的工单</li>
                    <li>可修改所有字段</li>
                  </ul>
                </div>
                <div v-if="isSgAdmin" style="margin-top: 10px">
                  <p><strong>施工单位管理人员/安全员：</strong></p>
                  <ul style="font-size: 12px; margin: 5px 0">
                    <li>可编辑整改相关字段</li>
                    <li>可修改状态：待整改→待审核→已整改</li>
                  </ul>
                </div>
                <div v-if="isJlAdmin" style="margin-top: 10px">
                  <p><strong>监理单位管理人员：</strong></p>
                  <ul style="font-size: 12px; margin: 5px 0">
                    <li>仅可审核工单</li>
                    <li>可修改状态：待审核→已整改/已取消</li>
                  </ul>
                </div>
              </div>
            </div>
          </ui-flex>
        </ui-tab-pane>
      </ui-tabs>
    </ui-stage>
  </div>
</template>

<script>
import { _ } from '@yishitec/web';
import { adminEdit } from '@yishitec/web/mixins';
import permissions from '../../../mixins/permissions';

export default {
  name: 'CitizenComplaintsEdit',
  mixins: [adminEdit, permissions],
  data() {
    return {
      withCustomFields: false,
      vLockedFields: ['project_id'],
      statusOptions: ['待整改', '待审核', '已整改', '已取消'],
    };
  },
  computed: {
    canEdit() {
      /** @type {This} */
      const that = this;
      const projectId = _.get(this.data, 'project_id');
      return (
        that.amI(projectId, '建设单位', '管理人员', true) ||
        that.amI(projectId, '施工单位', '安全员', true) ||
        that.amI(projectId, '施工单位', '管理人员', true) ||
        that.amI(projectId, '监理单位', '管理人员', true)
      );
    },
    canDelete() {
      /** @type {This} */
      const that = this;
      const projectId = _.get(this.data, 'project_id');
      // 仅建设单位管理人员可以删除
      return that.amI(projectId, '建设单位', '管理人员', true);
    },
    canAudit() {
      /** @type {This} */
      const that = this;
      const projectId = _.get(this.data, 'project_id');
      // 监理单位管理人员可以审核
      return that.amI(projectId, '监理单位', '管理人员', true);
    },
    canEditDescription() {
      /** @type {This} */
      const that = this;
      const projectId = _.get(this.data, 'project_id');
      // 建设单位可以编辑描述
      return that.amI(projectId, '建设单位', '管理人员', true);
    },
    canEditRectification() {
      /** @type {This} */
      const that = this;
      const projectId = _.get(this.data, 'project_id');
      // 施工单位可以编辑整改相关字段
      return (
        that.amI(projectId, '施工单位', '安全员', true) ||
        that.amI(projectId, '施工单位', '管理人员', true)
      );
    },
    canChangeStatus() {
      /** @type {This} */
      const that = this;
      const projectId = _.get(this.data, 'project_id');
      // 所有角色都可以修改状态，但根据角色不同有不同的权限
      return (
        that.amI(projectId, '建设单位', '管理人员', true) ||
        that.amI(projectId, '施工单位', '安全员', true) ||
        that.amI(projectId, '施工单位', '管理人员', true) ||
        that.amI(projectId, '监理单位', '管理人员', true)
      );
    },
    isJsAdmin() {
      /** @type {This} */
      const that = this;
      const projectId = _.get(this.data, 'project_id');
      return that.amI(projectId, '建设单位', '管理人员', true);
    },
    isSgAdmin() {
      /** @type {This} */
      const that = this;
      const projectId = _.get(this.data, 'project_id');
      return (
        that.amI(projectId, '施工单位', '安全员', true) ||
        that.amI(projectId, '施工单位', '管理人员', true)
      );
    },
    isJlAdmin() {
      /** @type {This} */
      const that = this;
      const projectId = _.get(this.data, 'project_id');
      return that.amI(projectId, '监理单位', '管理人员', true);
    },
    vAttributes() {
      // 根据角色过滤可编辑的字段
      const allFields = this.attributes;
      const filteredFields = [];

      for (const field of allFields) {
        let canEditField = true;

        // 根据字段名和角色权限过滤
        if (field.name === 'description' && !this.canEditDescription) {
          canEditField = false;
        }
        if (
          (field.name === 'rectificationIssue' ||
            field.name === 'rectificationProgress' ||
            field.name === 'rectificationPhotos') &&
          !this.canEditRectification
        ) {
          canEditField = false;
        }

        if (!canEditField) {
          field.readonly = true;
        }
        filteredFields.push(field);
      }

      return filteredFields;
    },
  },
  methods: {
    async handleAudit() {
      const res = await this.withLoading(this.api(`/api/citizen-complaints/audit/${this.getId()}`));
      if (!res) return;
      this.$message({
        type: 'success',
        message: '审核成功',
      });
      this.fetchData();
    },
  },
};
</script>

<style lang="less" scoped>
ul {
  padding-left: 15px;
}
</style>
